import MenuIcon from '@mui/icons-material/Menu'
import { IconButton, Menu, MenuItem } from '@mui/material'
import clsx from 'clsx'
import { FmsNotifications } from 'components/FmsNotifications/FmsNotifications'
import { useTheme } from 'context/ThemeContext'
import { useIsMobile } from 'hooks/useIsMobile'
import { useUser } from 'hooks/user'
import { ReactComponent as LogoMainSvg } from 'images/logo_main.svg'
import { ReactComponent as LogoNightSvg } from 'images/logo_night.svg'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useMatch } from 'react-router-dom'
import { PATH } from 'utils/constants'

import User from './components/User/User'

import style from '../Header.module.css'

const MainHeader = () => {
  const { theme } = useTheme()
  const { t } = useTranslation()
  const tabMissionControl = useMatch(`${PATH.MISSION_CONTROL}/*`)
  const tabFacilities = useMatch(`${PATH.FACILITY_LIST}/*`)
  const tabAssets = useMatch({ path: PATH.ASSET_LIST, end: true })
  const tabRecordedRoutes = useMatch(`${PATH.RECORDED_ROUTE_LIST}/*`)
  const tabMissionPlanner = useMatch(`${PATH.MISSION_LIST}/*`)
  const tabScenario = useMatch(`${PATH.SCENARIO_LIST}/*`)
  const tabMaps = useMatch(`${PATH.MAP_LIST}`)
  const tabTasks = useMatch(PATH.CALENDAR)
  const tabTasksLegacy = useMatch(PATH.CALENDAR_LEGACY)
  const tabScheduler = Boolean(tabTasks) || Boolean(tabTasksLegacy)
  const tabDashboard = useMatch(`${PATH.ANALYTICS}`)
  const user = useUser()
  const isMobile = useIsMobile()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const links = [
    { to: PATH.FACILITY_LIST, text: t('header.facilities'), active: !!tabFacilities },
    { to: PATH.ASSET_LIST, text: t('header.assets'), active: !!tabAssets },
    { to: PATH.CALENDAR, text: t('header.calendar'), active: tabScheduler },
    { to: PATH.ANALYTICS, text: t('header.analytics'), active: !!tabDashboard },
    { to: PATH.SCENARIO_LIST, text: t('header.scenarios'), active: !!tabScenario },
    { to: PATH.MISSION_LIST, text: t('header.missions'), active: !!tabMissionPlanner },
    { to: PATH.RECORDED_ROUTE_LIST, text: t('header.recorded_routes'), active: !!tabRecordedRoutes },
    { to: PATH.MAP_LIST, text: t('header.maps'), active: !!tabMaps },
  ]

  const mobileLinks = [
    ...(tabMissionControl ? [{ to: '#', text: t('header.mission_control'), active: true }] : []),
    ...links.filter(link => link.to !== PATH.MISSION_LIST && link.to !== PATH.RECORDED_ROUTE_LIST),
  ]

  return (
    <div className={style.header}>
      <Link to={PATH.ASSET_LIST} className={style.logo}>
        {theme === 'dark' ? <LogoNightSvg /> : <LogoMainSvg />}
      </Link>
      <div className={style.middle}>
        {!isMobile && (
          <div className={style.middleLinksBlock}>
            {tabMissionControl && (
              <div className={clsx(style.link, style.noCursor, style.active)}>{t('header.mission_control')}</div>
            )}
            {links.map(link => (
              <Link key={link.to} to={link.to} className={clsx(style.link, { [style.active]: link.active })}>
                {link.text}
              </Link>
            ))}
          </div>
        )}
      </div>
      <div className={style.user}>
        {isMobile && (
          <>
            <IconButton
              onClick={handleOpenMenu}
              className={style.burgerButton}
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseMenu}
              autoFocus={false}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
                autoFocusItem: false,
              }}
              PaperProps={{
                sx: {
                  background: theme === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: 'var(--glass-border)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  '& .MuiMenuItem-root': {
                    padding: '14px 24px',
                    color: 'var(--text-main)',
                    fontFamily: 'var(--font-main)',
                    fontSize: '15px',
                    '&:hover': {
                      background: 'rgba(46, 112, 239, 0.1)',
                    },
                    '&.active': {
                      color: 'var(--color-primary)',
                      fontWeight: 700,
                    },
                  },
                },
              }}
            >
              {mobileLinks.map(link => (
                <MenuItem
                  key={link.to}
                  onClick={handleCloseMenu}
                  component={Link}
                  to={link.to}
                  className={clsx({ active: link.active })}
                >
                  {link.text}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        <FmsNotifications />
        <User
          email={user?.state.email}
          firstName={user?.state.firstName}
          lastName={user?.state.lastName}
          avatarUrl={user?.state.avatarUrl}
        />
      </div>
    </div>
  )
}
export default MainHeader
